import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';

interface Subscriber {
  id: number;
  email: string;
  language: string;
  status: string;
  created_at: string;
}

export default function NewsletterPage() {
  const { t } = useTranslation();
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [sendResult, setSendResult] = useState<string>('');
  const [sendOk, setSendOk] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'aktiv' | 'inaktiv'>('all');

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase.from('newsletter_subscribers').select('*').order('created_at', { ascending: false });
      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }
      const { data, error: err } = await query;
      if (err) throw err;
      setSubscribers(data || []);
    } catch {
      setError(t('adm2.subscribers_load_error'));
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const exportCSV = () => {
    const headers = [t('adm2.csv_email'), t('adm2.csv_language'), t('adm2.csv_status'), t('adm2.csv_date')];
    const rows = subscribers.map((s) => [
      s.email,
      s.language,
      s.status,
      new Date(s.created_at).toLocaleDateString('de-CH'),
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `esteva-newsletter-abonnenten-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleSendNewsletter = async (testOnly: boolean) => {
    if (!subject.trim() || !body.trim()) {
      setSendOk(false);
      setSendResult(t('adm2.enter_subject_body'));
      return;
    }
    setSendLoading(true);
    setSendResult('');

    try {
      const edgeUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-newsletter`;
      const response = await fetch(edgeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          subject: subject.trim(),
          body: body.trim(),
          testOnly,
          testEmail: testOnly ? testEmail : undefined,
        }),
      });

      const result = await response.json();
      if (response.ok) {
        setSendOk(true);
        setSendResult(
          testOnly
            ? t('adm2.test_email_sent', { email: testEmail })
            : t('adm2.newsletter_sent', { sent: result.sent, failed: result.failed })
        );
      } else {
        setSendOk(false);
        setSendResult(result.error || t('adm2.send_error'));
      }
    } catch {
      setSendOk(false);
      setSendResult(t('adm2.connection_error'));
    } finally {
      setSendLoading(false);
    }
  };

  const toggleStatus = async (id: number, currentStatus: string) => {
    const newStatus = currentStatus === 'aktiv' ? 'inaktiv' : 'aktiv';
    try {
      const { error: err } = await supabase
        .from('newsletter_subscribers')
        .update({ status: newStatus })
        .eq('id', id);
      if (err) throw err;
      setSubscribers((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status: newStatus } : s))
      );
    } catch {
      setError(t('adm2.status_update_error'));
    }
  };

  const deleteSubscriber = async (id: number) => {
    if (!confirm(t('adm2.confirm_delete'))) return;
    try {
      const { error: err } = await supabase
        .from('newsletter_subscribers')
        .delete()
        .eq('id', id);
      if (err) throw err;
      setSubscribers((prev) => prev.filter((s) => s.id !== id));
    } catch {
      setError(t('adm2.subscriber_delete_error'));
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
        {t('adm2.newsletter')}
      </h1>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-background-50 rounded-xl p-5 border border-background-200">
          <p className="text-xs text-foreground-400 font-label uppercase tracking-wider mb-1">{t('adm2.total')}</p>
          <p className="text-2xl font-bold text-foreground-900 font-heading">{subscribers.length}</p>
        </div>
        <div className="bg-background-50 rounded-xl p-5 border border-background-200">
          <p className="text-xs text-foreground-400 font-label uppercase tracking-wider mb-1">{t('adm2.active')}</p>
          <p className="text-2xl font-bold text-primary-600 font-heading">
            {subscribers.filter((s) => s.status === 'aktiv').length}
          </p>
        </div>
        <div className="bg-background-50 rounded-xl p-5 border border-background-200">
          <p className="text-xs text-foreground-400 font-label uppercase tracking-wider mb-1">{t('adm2.inactive')}</p>
          <p className="text-2xl font-bold text-foreground-500 font-heading">
            {subscribers.filter((s) => s.status === 'inaktiv').length}
          </p>
        </div>
      </div>

      {/* Compose Newsletter */}
      <div className="bg-background-50 rounded-xl border border-background-200 p-5 md:p-6 mb-8">
        <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4 flex items-center gap-2">
          <i className="ri-mail-send-line text-primary-500"></i>
          {t('adm2.compose_newsletter')}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-label text-foreground-500 mb-1.5">{t('adm2.subject')}</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('adm2.subject_ph')}
              className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm font-body text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-label text-foreground-500 mb-1.5">{t('adm2.content')}</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t('adm2.newsletter_body_ph')}
              rows={6}
              maxLength={5000}
              className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm font-body text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-y"
            />
            <p className="text-xs text-foreground-400 mt-1">{t('adm2.chars_count', { count: body.length })}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="block text-xs font-label text-foreground-500 mb-1.5">{t('adm2.test_email')}</label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm font-body text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <button
                onClick={() => handleSendNewsletter(true)}
                disabled={sendLoading || !testEmail}
                className="w-full sm:w-auto px-4 py-2 rounded-lg border border-primary-300 text-primary-600 text-xs font-label font-semibold hover:bg-primary-50 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {t('adm2.send_test')}
              </button>
              <button
                onClick={() => handleSendNewsletter(false)}
                disabled={sendLoading}
                className="w-full sm:w-auto px-4 py-2 rounded-lg bg-primary-500 text-white text-xs font-label font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5"
              >
                {sendLoading && <i className="ri-loader-4-line animate-spin"></i>}
                {t('adm2.send_to_all')}
              </button>
            </div>
          </div>
          {sendResult && (
            <p className={`text-xs font-body ${sendOk ? 'text-accent-600' : 'text-red-500'}`}>
              {sendResult}
            </p>
          )}
        </div>
      </div>

      {/* Subscribers List */}
      <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 md:p-5 border-b border-background-200">
          <h2 className="font-heading text-lg font-bold text-foreground-900 flex items-center gap-2">
            <i className="ri-group-line text-primary-500"></i>
            {t('adm2.subscribers')}
          </h2>
          <div className="flex items-center gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'all' | 'aktiv' | 'inaktiv')}
              className="px-3 py-1.5 rounded-lg border border-background-200 text-xs font-body text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer"
            >
              <option value="all">{t('adm2.filter_all')}</option>
              <option value="aktiv">{t('adm2.active')}</option>
              <option value="inaktiv">{t('adm2.inactive')}</option>
            </select>
            <button
              onClick={exportCSV}
              disabled={subscribers.length === 0}
              className="px-3 py-1.5 rounded-lg border border-background-200 text-xs font-label text-foreground-600 hover:bg-background-100 transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-download-line"></i>
              CSV
            </button>
          </div>
        </div>

        {error && (
          <div className="p-4 text-sm text-red-500 font-body bg-red-50">
            {error}
          </div>
        )}

        {loading ? (
          <div className="p-8 text-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-foreground-300"></i>
          </div>
        ) : subscribers.length === 0 ? (
          <div className="p-8 text-center text-sm text-foreground-400 font-body">
            {t('adm2.no_subscribers')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider">{t('adm2.col_email')}</th>
                  <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider">{t('adm2.col_language')}</th>
                  <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider">{t('adm2.col_status')}</th>
                  <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider">{t('adm2.col_date')}</th>
                  <th className="text-right px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider">{t('adm2.col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((s) => (
                  <tr key={s.id} className="border-b border-background-100 hover:bg-background-100/50 transition-colors">
                    <td className="px-4 py-3 text-sm font-body text-foreground-800">{s.email}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-label px-2 py-1 rounded-full bg-secondary-100 text-secondary-800 uppercase">
                        {s.language}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleStatus(s.id, s.status)}
                        className={`text-xs font-label px-2 py-1 rounded-full transition-colors cursor-pointer ${
                          s.status === 'aktiv'
                            ? 'bg-accent-100 text-accent-800'
                            : 'bg-background-100 text-foreground-500'
                        }`}
                      >
                        {s.status === 'aktiv' ? t('adm2.active') : t('adm2.inactive')}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs font-body text-foreground-500">
                      {new Date(s.created_at).toLocaleDateString('de-CH')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => deleteSubscriber(s.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                        title={t('adm2.delete')}
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}